/**
 * Realtime contract — the ONE definition of what an Obrava change notification is,
 * shared by the backoffice, the planta app and the Postgres triggers that publish it.
 *
 * THE TWO RULES THIS FILE ENCODES
 * ------------------------------
 * 1. **Signal, not payload.** An event carries only enough to answer "what kind of thing
 *    changed?" — never the thing itself. No names, no quantities, no statuses. Every client
 *    re-reads the authoritative row through its normal, RLS-scoped path (PostgREST in the
 *    web apps, `runPull` on a device).
 *
 *    That is a SECURITY property, not a style preference: because the message body holds no
 *    domain data, a message delivered to the wrong subscriber — or forged by one — cannot
 *    leak anything. It can only cause a re-read, and the re-read is filtered by RLS anyway.
 *    Keep it that way; the moment a payload starts carrying business fields, the channel
 *    becomes a second, unaudited data path around RLS.
 *
 * 2. **One channel per obrador, one event name.** `tenant:<tenant_id>` / `data_changed`.
 *    The InsForge SDK registers listeners GLOBALLY by event name (`realtime.on(event, cb)`
 *    keys a map by the event string, not by channel), so many event names would mean many
 *    registrations and manual disambiguation via `meta.channel`. One name plus a `topic`
 *    discriminator in the body gives a single, clean dispatcher instead.
 *
 *    Splitting later into `tenant:<id>:<topic>` is a transport change that does NOT alter
 *    this contract — the `topic` already travels in the body. Starting coarse is
 *    sequencing, not a dead end.
 */

import { z } from 'zod';

/**
 * Functional domains. A subscriber filters on these to decide whether it cares at all,
 * without having to know every table name.
 *
 * Planta, for instance, reacts to `recipes` / `production` / `catalog` / `purchasing` /
 * `sales` and ignores the rest: a stock movement made by another device does not need to
 * wake a pull on this one.
 */
export const REALTIME_TOPICS = [
  /** Articles and the master data that describes them. */
  'catalog',
  /** Recipes and their immutable versions. */
  'recipes',
  /** Work orders and everything produced while executing them. */
  'production',
  /** Stock movements and balances. */
  'stock',
  /** Purchase orders and goods-in. */
  'purchasing',
  /** Sales orders and expeditions. */
  'sales',
  /** Incidents reported from the shop floor. */
  'quality',
  /** Tenant configuration, users and roles. */
  'settings',
] as const;
export type RealtimeTopic = (typeof REALTIME_TOPICS)[number];

/** The single event name published on a tenant channel. */
export const REALTIME_EVENT = 'data_changed';

/** The channel a given obrador's changes are published on. */
export function tenantChannel(tenantId: string): string {
  return `tenant:${tenantId}`;
}

/**
 * Which topic a table belongs to — the SAME mapping the Postgres trigger arguments use
 * (see the realtime migration). Kept here so a client can reason about a table name it
 * receives without a second, drifting copy of the list.
 *
 * FIRST WAVE ONLY. The criterion for inclusion is not volume, it is whether a change
 * CROSSES AN APP BOUNDARY — something a person on another platform needs to see. The long
 * tail (catalog sub-tables, settings masters, document lines) is deliberately absent:
 * nobody is harmed by those taking 30s to refresh. Adding one is a line here plus a line
 * in the migration's array.
 */
export const REALTIME_TABLE_TOPICS: Readonly<Record<string, RealtimeTopic>> = {
  // production — the shop floor, in both directions
  work_orders: 'production',
  work_order_assignments: 'production',
  step_records: 'production',
  finished_lots: 'production',
  // The shop floor reporting its progress. Added after the first wave: marking a step or
  // recording a weighing does not touch work_orders, so without these the backoffice saw
  // nothing move on an OT it was watching until the OT was closed.
  work_order_steps: 'production',
  weight_records: 'production',
  // recipes — the critical one: a new version must reach the device
  recipes: 'recipes',
  recipe_versions: 'recipes',
  // catalog — shelf life, units, what ends up on the label
  articles: 'catalog',
  // stock — drives the Existències screen live
  stock_movements: 'stock',
  // A lot blocked or unblocked by hand (OBR-14) must reach the other screens and devices at once.
  stock_state_changes: 'stock',
  // A stock count opened, saved, validated or cancelled (OBR-147): only its header publishes (its lines can be thousands).
  stock_counts: 'stock',
  // purchasing / sales — planta ↔ backoffice documents
  purchase_orders: 'purchasing',
  receptions: 'purchasing',
  sales_orders: 'sales',
  shipments: 'sales',
  // quality — already had its own channel before this contract existed
  incidents: 'quality',
  // settings — who a person is and what they may open (OBR-25). A role granted or a menu
  // option hidden by an administrator must reach that person's open session, not wait
  // for a reload: the backoffice re-reads the person's access when either one changes.
  user_menu_access: 'settings',
  user_roles: 'settings',
};

/** Every table that publishes in the first wave. */
export const REALTIME_TABLES = Object.keys(REALTIME_TABLE_TOPICS);

/** The write that produced an event. */
export const REALTIME_OPERATIONS = ['insert', 'update', 'delete'] as const;
export type RealtimeOperation = (typeof REALTIME_OPERATIONS)[number];

/**
 * The body of a `data_changed` event.
 *
 * Deliberately minimal — see rule 1 above.
 *
 * Unknown fields are STRIPPED (zod's default), and that is the enforcement of rule 1 on
 * the receiving end: even if a publisher were changed to leak a domain field, it would be
 * dropped here and never reach application code. Strip is chosen over `.strict()` on
 * purpose — a strict schema would make an older client REJECT a message from a newer
 * publisher that legitimately added a field, turning a routine rollout into a realtime
 * outage for everyone who had not updated yet.
 */
export const dataChangedSchema = z.object({
  /** The table that changed, e.g. 'work_orders'. */
  table: z.string().min(1),
  /** Its functional domain, so a subscriber can filter without knowing the table. */
  topic: z.enum(REALTIME_TOPICS),
  /** Which write it was. */
  op: z.enum(REALTIME_OPERATIONS),
  /** The row's id. Null only if the row somehow carries none. */
  id: z.string().uuid().nullable(),
  /**
   * Who caused it (`auth.uid()`), or null for a system/service path.
   *
   * Use this for UI manners ONLY — do not chime or toast at someone for their own action.
   * Do NOT use it to skip an invalidation: a re-read is cheap and always correct, whereas
   * skipping one leaves a ghost row on screen whenever a mutation half-fails.
   */
  actor_id: z.string().uuid().nullable(),
  /** Server timestamp (ISO-8601), for ordering and diagnostics. */
  at: z.string(),
});

export type DataChangedEvent = z.infer<typeof dataChangedSchema>;

/**
 * The realtime envelope the InsForge SDK wraps every message in. The publisher spreads the
 * event body alongside this, so an incoming message is `{ ...DataChangedEvent, meta }`.
 */
export interface RealtimeMeta {
  readonly channel?: string;
  readonly messageId: string;
  readonly senderType: 'system' | 'user';
  readonly senderId?: string;
  readonly timestamp: string;
}

/**
 * Decode a raw socket message into a `DataChangedEvent`, or null if it is not one.
 *
 * Returning null rather than throwing is the point: a malformed or unknown message must
 * never break a subscriber's loop. Realtime is an enhancement — a client that cannot make
 * sense of a message simply carries on with its polled/heartbeat reads.
 *
 * ONLY SERVER-PUBLISHED EVENTS ARE ACCEPTED
 * -----------------------------------------
 * A legitimate `data_changed` is published by a SECURITY DEFINER Postgres trigger, and the
 * realtime server stamps those `meta.senderType === 'system'`. Anything a CLIENT publishes
 * is stamped `'user'` (with a `senderId`). The stamp is applied server-side from the
 * connection's identity — a publisher cannot choose it — so it is trustworthy.
 *
 * This check is what actually closes the "clients can publish" gap. The intended fix was
 * RLS on `realtime.messages`, but that table is InsForge-owned infrastructure and
 * `ALTER TABLE` there is refused with `must be owner of table messages` — so the hole
 * cannot be closed from a migration at all. Rejecting non-system senders here closes it
 * from the receiving end instead, which is where it actually matters: a forged event now
 * changes nothing, because no subscriber acts on it.
 *
 * A message with no `meta` at all is accepted: the envelope is added by the server on
 * delivery, so its absence means this is not a delivered message (a fixture, a direct
 * call in a test), not an unauthenticated one.
 */
export function parseDataChanged(message: unknown): DataChangedEvent | null {
  if (message === null || typeof message !== 'object') return null;
  // Strip the SDK envelope: the body is spread alongside `meta`.
  const { meta, ...body } = message as Record<string, unknown>;

  if (meta !== null && typeof meta === 'object') {
    const senderType = (meta as Record<string, unknown>).senderType;
    // Present but not 'system' => published by a client. Never act on it.
    if (senderType !== undefined && senderType !== 'system') return null;
  }

  const parsed = dataChangedSchema.safeParse(body);
  return parsed.success ? parsed.data : null;
}
